
import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { useSEO } from '../../utils/seo';

const services = [
  { title: 'Medical Website Design', path: '/services/medical-website-design', icon: 'ri-layout-line' },
  { title: 'Medical SEO & Local SEO', path: '/services/medical-seo', icon: 'ri-search-line' },
  { title: 'Healthcare PPC Advertising', path: '/services/healthcare-ppc', icon: 'ri-advertisement-line' },
  { title: 'Content Marketing', path: '/services/content-marketing', icon: 'ri-article-line' },
  { title: 'Social Media Marketing', path: '/services/social-media-marketing', icon: 'ri-instagram-line' },
  { title: 'Reputation Management', path: '/services/reputation-management', icon: 'ri-star-line' },
  { title: 'Marketing Automation', path: '/services/marketing-automation', icon: 'ri-robot-line' },
  { title: 'AI Lead Capture', path: '/services/ai-lead-capture', icon: 'ri-customer-service-line' },
  { title: 'Analytics & Reporting', path: '/services/analytics-reporting', icon: 'ri-bar-chart-box-line' },
];

const industries = [
  { title: 'OB-GYN & Menopause', path: '/industries/obgyn-menopause', icon: 'ri-women-line' },
  { title: 'Preventive Medicine', path: '/industries/preventive-medicine', icon: 'ri-shield-cross-line' },
  { title: 'Functional & Integrative', path: '/industries/functional-integrative', icon: 'ri-leaf-line' },
  { title: 'Longevity & Anti-Aging', path: '/industries/longevity-antiaging', icon: 'ri-timer-line' },
  { title: 'BHRT & Hormone Therapy', path: '/industries/bhrt-hormone-therapy', icon: 'ri-flask-line' },
  { title: 'Cardiology', path: '/industries/cardiology', icon: 'ri-heart-pulse-line' },
  { title: 'Endocrinology', path: '/industries/endocrinology', icon: 'ri-test-tube-line' },
  { title: 'Urology', path: '/industries/urology', icon: 'ri-men-line' },
  { title: 'Medical Spas', path: '/industries/medical-spas', icon: 'ri-sparkling-line' },
  { title: 'Wellness Centers', path: '/industries/wellness-centers', icon: 'ri-mental-health-line' },
  { title: 'Primary Care & Prevention', path: '/industries/primary-care-prevention', icon: 'ri-stethoscope-line' },
  { title: 'Nurse Practitioners', path: '/industries/nurse-practitioners', icon: 'ri-nurse-line' },
  { title: 'Physician Assistants', path: '/industries/physician-assistants', icon: 'ri-user-heart-line' },
  { title: 'Clinic Owners & Directors', path: '/industries/clinic-owners-directors', icon: 'ri-building-2-line' },
];

const company = [
  { title: 'Home', path: '/', icon: 'ri-home-line' },
  { title: 'About Us', path: '/about', icon: 'ri-information-line' },
  { title: 'Automation Platform', path: '/automation-platform', icon: 'ri-settings-line' },
  { title: 'Book a Strategy Call', path: '/book-strategy-call', icon: 'ri-calendar-check-line' },
];

export default function SitemapPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useSEO({
    title: 'Sitemap | MillionDollarMed | Healthcare Marketing Agency',
    description:
      'Browse all pages on MillionDollarMed. Find healthcare marketing services, industry specialties, and company information. Navigate our complete site directory.',
    keywords:
      'sitemap, MillionDollarMed pages, healthcare marketing services, medical marketing directory',
    canonical: `${siteUrl}/sitemap`,
  });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-16 px-6 lg:px-12 bg-gradient-to-br from-[#7C3AED] to-[#A855F7] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        <div className="max-w-7xl mx-auto relative z-10 text-center">
          <h1 className="text-5xl lg:text-6xl font-bold text-white leading-tight mb-4">Sitemap</h1>
          <p className="text-lg text-white/90 max-w-2xl mx-auto">
            Browse all pages across our healthcare marketing website. Find services, industries, and company
            information.
          </p>
        </div>
      </section>

      {/* Sitemap Content */}
      <section className="py-20 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Company Pages */}
            <div>
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 bg-gradient-to-br from-[#7C3AED] to-[#A855F7] rounded-lg flex items-center justify-center">
                  <i className="ri-building-line text-white text-lg"></i>
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Company</h2>
              </div>
              <ul className="space-y-4">
                {company.map((item) => (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      className="flex items-center gap-3 text-gray-700 hover:text-[#7C3AED] transition-colors group"
                    >
                      <div className="w-8 h-8 bg-[#7C3AED]/10 rounded-md flex items-center justify-center group-hover:bg-[#7C3AED]/20 transition-colors">
                        <i className={`${item.icon} text-[#7C3AED] text-sm`}></i>
                      </div>
                      <span className="text-sm font-medium">{item.title}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Services */}
            <div>
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 bg-gradient-to-br from-[#7C3AED] to-[#A855F7] rounded-lg flex items-center justify-center">
                  <i className="ri-service-line text-white text-lg"></i>
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Services</h2>
              </div>
              <ul className="space-y-4">
                {services.map((item) => (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      className="flex items-center gap-3 text-gray-700 hover:text-[#7C3AED] transition-colors group"
                    >
                      <div className="w-8 h-8 bg-[#7C3AED]/10 rounded-md flex items-center justify-center group-hover:bg-[#7C3AED]/20 transition-colors">
                        <i className={`${item.icon} text-[#7C3AED] text-sm`}></i>
                      </div>
                      <span className="text-sm font-medium">{item.title}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Industries */}
            <div>
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 bg-gradient-to-br from-[#7C3AED] to-[#A855F7] rounded-lg flex items-center justify-center">
                  <i className="ri-hospital-line text-white text-lg"></i>
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Industries</h2>
              </div>
              <ul className="space-y-4">
                {industries.map((item) => (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      className="flex items-center gap-3 text-gray-700 hover:text-[#7C3AED] transition-colors group"
                    >
                      <div className="w-8 h-8 bg-[#7C3AED]/10 rounded-md flex items-center justify-center group-hover:bg-[#7C3AED]/20 transition-colors">
                        <i className={`${item.icon} text-[#7C3AED] text-sm`}></i>
                      </div>
                      <span className="text-sm font-medium">{item.title}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 lg:px-12 bg-gradient-to-br from-[#7C3AED] to-[#A855F7] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">Ready to Grow Your Practice?</h2>
          <p className="text-white/90 mb-8">
            Book a free strategy call and discover how HIPAA-compliant marketing automation can transform your
            patient acquisition.
          </p>
          <Link
            to="/book-strategy-call"
            className="inline-flex items-center gap-2 bg-white text-[#7C3AED] px-8 py-4 rounded-lg hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap font-bold"
          >
            <span>Book Your Free Strategy Call</span>
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
